import { ComponentType } from "react";
import { UserAccess } from "../../../hooks/useUserAccess";
import { WidgetFilterValues } from "../prefs/homePrefs";

export type ScopeKind = "mine" | "all" | "managers";

export interface SelectOption {
  label: string;
  value: string;
}

/** One editable filter in a card's filter dialog. */
export type FilterField =
  | {
      key: string;
      type: "multiselect";
      label: string;
      /** Static options; otherwise the card supplies them via `onOptions`. */
      options?: SelectOption[];
      placeholder?: string;
    }
  | { key: string; type: "select"; label: string; options: SelectOption[] }
  | { key: string; type: "toggle"; label: string; help?: string };

export interface WidgetComponentProps {
  access: UserAccess;
  /** Effective filters: role defaults merged with the user's saved overrides. */
  filters: WidgetFilterValues;
  /** Report filter options discovered from the data (e.g. languages). */
  onOptions: (key: string, options: SelectOption[]) => void;
}

export interface WidgetDefinition {
  id: string;
  title: string;
  description: string;
  /** PrimeIcons class, e.g. "pi pi-heart". */
  icon: string;
  /** Accent colour for the card header icon. */
  accent: string;
  /** Primary deep link for the tool. */
  path: string;
  requiredTool?: string;
  requiredRole?: string;
  /** Whether the card supports Mine / All / chosen managers. */
  supportsScope: boolean;
  /** Label for the "mine" scope, e.g. "My channels". */
  mineLabel?: string;
  filterFields: FilterField[];
  /** Defaults for non-scope filters. */
  defaultFilters: WidgetFilterValues;
  /** Wider cards span two grid columns on large screens. */
  wide?: boolean;
  Component: ComponentType<WidgetComponentProps>;
}

export const SCOPE_KEY = "scope";
export const MANAGERS_KEY = "managerEmails";

/** Users start on their own issues; admins start on everything. */
export const defaultScopeFor = (isAdmin: boolean): ScopeKind => (isAdmin ? "all" : "mine");

export const effectiveFilters = (
  def: WidgetDefinition,
  isAdmin: boolean,
  saved: WidgetFilterValues | undefined
): WidgetFilterValues => ({
  ...(def.supportsScope ? { [SCOPE_KEY]: defaultScopeFor(isAdmin), [MANAGERS_KEY]: [] } : {}),
  ...def.defaultFilters,
  ...(saved ?? {}),
});

export const getScope = (filters: WidgetFilterValues): ScopeKind => {
  const v = filters[SCOPE_KEY];
  return v === "all" || v === "managers" ? v : "mine";
};

export const getStringArray = (filters: WidgetFilterValues, key: string): string[] => {
  const v = filters[key];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
};

/** Query params shared by every scoped summary endpoint. */
export const scopeParams = (filters: WidgetFilterValues): Record<string, string> => {
  const scope = getScope(filters);
  const params: Record<string, string> = { scope };
  if (scope === "managers") params.managerEmails = getStringArray(filters, MANAGERS_KEY).join(",");
  return params;
};

/** Lower-cased "is this row mine" check for client-side scoped cards. */
export const emailsInScope = (filters: WidgetFilterValues, myEmail: string | null): string[] | null => {
  const scope = getScope(filters);
  if (scope === "all") return null;
  if (scope === "managers") return getStringArray(filters, MANAGERS_KEY).map((e) => e.toLowerCase());
  return myEmail ? [myEmail.toLowerCase()] : [];
};

export const matchesEmails = (allowed: string[] | null, rowEmails: unknown): boolean => {
  if (allowed === null) return true;
  const list = Array.isArray(rowEmails)
    ? rowEmails
    : typeof rowEmails === "string"
      ? rowEmails.split(",")
      : [];
  return list.some((e) => allowed.includes(String(e).trim().toLowerCase()));
};
